import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Pill, PhoneCall } from "lucide-react";
import { OUTCOMES } from "@/components/RefillNotes";

const LastRefillCall = ({ meds }) => {
  const logs = meds.flatMap((m) => m.refill_log || []).sort((a, b) => (a.at < b.at ? 1 : -1));
  const l = logs[0];
  if (!l) return null;
  const today = new Date().toLocaleDateString("en-CA");
  return (
    <span data-testid="patient-last-refill-call" title={`${logs.length} refill call${logs.length === 1 ? "" : "s"} logged`}
      className="inline-flex flex-wrap items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] text-slate-600">
      <PhoneCall className="h-3 w-3 text-cyan-600" /><b className="font-semibold">{OUTCOMES[l.outcome]}</b>{l.note ? ` — ${l.note}` : ""}
      <span className="text-slate-400">{l.by} · {fmtDate(l.at)}</span>
      {l.callback_date && <span data-testid="patient-refill-callback" className={`rounded px-1.5 font-semibold ${l.callback_date <= today ? "bg-cyan-600 text-white" : "bg-slate-100 text-slate-600"}`}>
        {l.callback_date <= today ? "Call back today" : `Call back ${fmtDate(`${l.callback_date}T12:00:00`)}`}</span>}
    </span>
  );
};

export const PatientMedications = ({ patientId, className = "" }) => {
  const { data = [], isLoading } = useQuery({ queryKey: ["medications", "patient", patientId], enabled: !!patientId,
    queryFn: async () => (await api.get(`/patients/${patientId}/medications`)).data });
  if (!patientId || isLoading) return null;

  return (
    <div data-testid="patient-medications" className={`flex flex-wrap items-center gap-2 ${className}`}>
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Medicines</span>
      {data.length === 0 && <span data-testid="patient-medications-empty" className="text-[11px] text-slate-400">None on file</span>}
      {data.map((m) => (
        <span key={m.id} data-testid={`patient-med-${m.id}`} title={[m.notes, m.note].filter(Boolean).join(" — ") || undefined}
          className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-semibold text-violet-800">
          {m.date && <span className="font-normal text-violet-500">{fmtDate(`${m.date}T12:00:00`)} ·</span>}
          <Pill className="h-3 w-3" />{m.medication}
          {(m.dose || m.frequency) && <span className="font-normal text-violet-600">· {[m.dose, m.frequency].filter(Boolean).join(", ")}</span>}
        </span>
      ))}
      <LastRefillCall meds={data} />
      <Link to="/medications" data-testid="patient-medications-manage" className="text-[11px] font-semibold text-[#0B1F44] hover:underline">Manage</Link>
    </div>
  );
};
